<template>
  <!-- eslint-disable max-len -->
  <BaseCard
    :id="`project-${project.name}`"
    ref="card"
    class="card project-card"
    :class="{ 'project-selected': selected }"
    :title="title"
    @edit-mode="(mode) => editTitle = mode"
    @apply-edit="handleApplyEdit"
  >
    <template #header>
      <IconButton
        :id="`project-${project.name}-menu-btn`"
        class="refresh-icon"
        :icon="'info'"
        :width="22"
        :height="22"
        @click="gotoProjectDetails"
      />
      <BaseMenu
        v-if="permissions && permissions.project_write"
        :placement="'bottom-start'"
        @closed="show_menu = false"
      >
        <IconButton
          :id="`project-${project.name}-menu-btn`"
          v-model="show_menu"
          class="refresh-icon"
          :icon="'more_vert'"
          :width="22"
          :height="22"
        />
        <template #menu="{closeMenu}">
          <ul>
            <li>
              <BaseMenuButton
                :id="`project-${project.name}-menu-edit`"
                v-tooltip="'Edit project name and description'"
                @click.stop="handleApplyEdit(), closeMenu()"
              >
                <SVGIcon
                  :iconName="'edit'"
                  :width="'22px'"
                  :height="'22px'"
                />
                <span>Edit</span>
              </BaseMenuButton>
            </li>
            <li v-if="isAdmin && project.organization_id === user.organization_id">
              <BaseMenuButton
                :id="`project-${project.name}-menu-manage-access`"
                v-tooltip="'View and edit project access'"
                @click.stop="handleManageAccess(), closeMenu()"
              >
                <SVGIcon
                  :iconName="'people'"
                  :width="'22px'"
                  :height="'22px'"
                />
                <span>Manage Access</span>
              </BaseMenuButton>
            </li>
            <li>
              <BaseMenuButton
                :id="`project-${project.name}-menu-recycle`"
                v-tooltip="'Move project to recycle bin. All data under this project will be moved as well'"
                class="_danger"
                @click.stop="deleteProject(), closeMenu()"
              >
                <SVGIcon
                  :iconName="'trash'"
                  :width="'22px'"
                  :height="'22px'"
                />
                <span>Move to Recycle Bin</span>
              </BaseMenuButton>
            </li>
          </ul>
        </template>
      </BaseMenu>
    </template>
    <template #body>
      <div class="project-rows">
        <div class="bar" />
        <div class="rows-container">
          <div
            v-tooltip="'Navigate to datasets'"
            :class="`${project.name}-datasets-btn datasets-btn`"
            class="project-row"
            @click="moveToHandlerPage('datasets')"
          >
            <div class="d-flex">
              <SVGIcon
                :iconName="'database2'"
                :width="'32px'"
                :height="'32px'"
                class="project-row__icon"
              />

              <div class="stack">
                <div class="stack__value-row">
                  <span>{{ project.no_of_datasets }}</span>
                  <label>Datasets</label>
                </div>
              </div>
            </div>
            <SVGIcon
              :iconName="'chevron_right'"
              :width="'24px'"
              :height="'24px'"
              class="project-row__icon"
            />
          </div>
          <div
            v-tooltip="'Navigate to auditing tasks'"
            :class="`${project.name}-auditing-tasks-btn auditing-tasks-btn`"
            class="project-row"
            @click="moveToHandlerPage('auditing tasks')"
          >
            <div class="d-flex">
              <SVGIcon
                :iconName="'tag'"
                :width="'32px'"
                :height="'32px'"
                class="project-row__icon"
              />

              <div class="stack">
                <div class="stack__value-row">
                  <span>{{ project.no_of_label_tasks }}</span>
                  <label>Auditing Tasks</label>
                </div>
              </div>
            </div>
            <SVGIcon
              :iconName="'chevron_right'"
              :width="'24px'"
              :height="'24px'"
              class="project-row__icon"
            />
          </div>
          <div
            v-tooltip="'Navigate to experiments'"
            :class="`${project.name}-experiments-btn experiments-btn`"
            class="project-row d-block"
            @click="moveToHandlerPage('experiments')"
          >
            <div class="d-flex w-100 justify-content-between align-items-center">
              <div class="d-flex">
                <SVGIcon
                  :iconName="'science'"
                  :width="'32px'"
                  :height="'32px'"
                  class="project-row__icon"
                />

                <div class="stack">
                  <div class="stack__value-row">
                    <span>{{ project.no_of_experiments }}</span>
                    <label>Model Experiments</label>
                  </div>
                </div>
              </div>
              <SVGIcon
                :iconName="'chevron_right'"
                :width="'24px'"
                :height="'24px'"
                class="project-row__icon"
              />
            </div>
            <div class="sub-row d-flex">
              <SVGIcon
                :iconName="'train'"
                :width="'32px'"
                :height="'32px'"
                class="project-row__icon"
              />

              <div class="stack">
                <div class="stack__value-row">
                  <span>{{ project.no_of_trainers }}</span>
                  <label>Training Sessions</label>
                </div>
              </div>
            </div>
            <div class="sub-row mt-1 d-flex">
              <SVGIcon
                :iconName="'compare'"
                :width="'32px'"
                :height="'32px'"
                class="project-row__icon"
              />

              <div class="stack">
                <div class="stack__value-row">
                  <span>{{ project.no_of_validations }}</span>
                  <label>Validate Sessions</label>
                </div>
              </div>
            </div>
          </div>
        </div>        
      </div>

      <DescriptionField style="margin-top: 10px;"
        :text="project.description"
        :clickable="true"
        @click="handleApplyEdit"
      />
    </template>
    <template #footer>
      <div class="footer">
        <EncodedIdField
          :id="project.id"
          :type="'project'"
        />
        <div class="footer-icons">
          <SVGIcon
            v-if="is3d"
            v-tooltip="'This dataset has 3D elements'"
            :iconName="'3d'"
            :width="'24px'"
            :height="'24px'"
            class="project-row__icon"
          />

          <SVGIcon
            v-if="isReadOnly"
            v-tooltip="'Read Only Project - Datasets must be copied to another project to edit.'"
            :iconName="'pencil_off'"
            :width="'24px'"
            :height="'24px'"
            class="project-row__icon"
          />

          <SVGIcon
            v-if="isPublic"
            v-tooltip="'Public Project - Everyone can view this project.'"
            :iconName="'earth'"
            :width="'24px'"
            :height="'24px'"
            class="project-row__icon"
          />
        </div>
      </div>
    </template>
  </BaseCard>
</template>
<script>
import BaseCard from '@/components/BaseCard.vue';
import BaseTextExpander from '@/components/BaseTextExpander.vue';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseMenuButton from '@/components/BaseMenuButton.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import IconButton from '@/components/IconButton.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import DescriptionField from '@/components/utils/DescriptionField.vue';

export default {
  components: {
    IconButton,
    BaseCard,
    BaseTextExpander,
    BaseMenu,
    BaseMenuButton,
    SVGIcon,
    EncodedIdField,
    DescriptionField,
  },
  props: {
    project: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: Boolean,
      default: false,
    },
    refresh: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    'delete-project': null,
    'clicked': null,
    'edited-project': null,
    'edit-project': null,
    'open-project-access-management': null,
    'go-to-project-details': null,
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      editTitle: false,
      datasets: [23, 44],
      permissions: null,
      show_menu: false,
      is3d: false,
      isPublic: false,
      isReadOnly: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    isDebugMode() {
      return this.$store.state.debugMode;
    },
    title() {
      return this.isDebugMode ? `${this.project.name} (${this.project.id})` : `${this.project.name}`;
    },

    isAdmin() {
      return this.user.role === 'admin' || this.user.role === 'super';
    },
  },
  created() {
    this.GetPermissionsForCurrentUser();
  },
  methods: {
    async GetPermissionsForCurrentUser() {
      this.isPublic = this.project.is_public;
      const user = this.$store.state.user.user;
      await this.dataConnect.getProjectAccessForSingleUser({
        project_id: this.project.id,
        username: user.username,
      })
        .then((data) => {
          if (data.result) {
            this.permissions = data.result;
            this.isReadOnly = !data.result['project_write'];
          }
        })
        .catch((e) => console.log(e));
    },
    gotoProjectDetails() {
      this.$emit('go-to-project-details', this.project.id);
    },
    deleteProject() {
      this.$emit('delete-project');
    },
    handleClickEvent() {
      this.$emit('clicked');
    },
    moveToHandlerPage(name) {
      this.handleClickEvent();
      if (name.toLowerCase() === "datasets") this.$router.push({ name: 'datasets' });
      if (name.toLowerCase() === "experiments") this.$router.push({ name: 'experiment', params: { projectId: this.project.id } });
      if (name.toLowerCase() === "automations") this.$router.push({ path: '/automation' });
      if (name.toLowerCase() === "auditing tasks") this.$router.push({ name: 'taskboard', params: { projectId: this.project.id } });
    },
    handleApplyEdit() {
      this.$emit('edit-project');
    },
    handleManageAccess() {
      this.$emit('open-project-access-management');
    },
  },
};
</script>

<style lang="scss" scoped>

.footer {
  display: flex;
  flex-direction: row;
  justify-content:space-between;
  width: 100%;
}

.footer-icons {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: fit-content;
}


.project-selected {
  @include themify() {
    outline: 3px solid themed('card-list-color-selected-border')
  }
}

.icon-container {
  display: flex;
  align-items: center;
  button + button {
    margin-left: 10px;
  }
}

.details {
  margin-top: auto;
  &__row {
    display: flex;
    flex-direction: row;
    margin-top: 8px;
  }
  label {
    width: fit-content;
    font-weight: 600;
    margin-right: 8px;
  }
  span {
    flex: 1 1 auto;
  }
}

.delete-project-icon,
.apply-edit-icon,
.edit-data-icon,
.database-icon {
  visibility: hidden;
}

.card:hover .database-icon,
.card:hover .apply-edit-icon,
.card:hover .edit-data-icon,
.card:hover .delete-project-icon {
  visibility: visible;
  @include themify() {
    color: themed('icon-color-inactive');
  }
}

.card:hover .database-icon:hover,
.card:hover .apply-edit-icon:hover,
.card:hover .edit-data-icon:hover {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.card:hover .delete-project-icon:hover {
  @include themify() {
    color: themed('color-red');
  }
}

.project-card {
  min-width: 300px;
  max-width: 500px;
  height: 100%;
}

.rows-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    gap: 10px;
}

.project-rows {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.project-row__icon {
  margin-left: 8px;
}

.project-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width:100%;
  height: fit-content;
  font-size: small;
  border-radius: 5px;
  cursor: pointer;
  &__icon {
    color: var(--color-primary);
  }
  padding: 10px;
  border-radius: 4px;
  box-shadow: 2px 2px 4px 2px rgba(0, 0, 0, 0.125), 0 0 2px 1px rgba(0, 0, 0, 0.25);
  align-items: center;
  justify-content: space-between;

  &:hover {
    cursor: pointer;
    background: rgba(140,140,140,0.2)
  }

}

.stack{
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 10px;

  &__value-row {
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: baseline;

    span {
      font-size: 1rem;
      font-weight: 700;
    }

    label {
      font-weight: 600;
      font-size: 0.875rem;
      cursor: inherit;
    }
  }
}

.sub-row {
  margin: 5px 0 5px 32px;
}

</style>
